import { deleteAccountErrorDetailsSchema, type DeleteAccountErrorDetails } from "@woven/contracts";
import { useLayoutEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "../../auth/context";
import { ControlApiError } from "../../lib/api";
import { getRequiredWebEnv, useControlApiClient } from "../../lib/hooks";
import { CapacityTooltip } from "../capacity/CapacityTooltip";
import { useAccountCapacity } from "../capacity/AccountCapacityContext";

const providerNames: Record<string, string> = {
  "google.com": "Google",
  "github.com": "GitHub",
  password: "Email and password",
};

function deletionSupportReason(error: unknown): DeleteAccountErrorDetails["deletionState"] | null {
  if (!(error instanceof ControlApiError) || error.code !== "UNAVAILABLE") return null;
  const parsed = deleteAccountErrorDetailsSchema.safeParse(error.details);
  return parsed.success ? parsed.data.deletionState : null;
}

const cleanupSupportMessages: Record<DeleteAccountErrorDetails["deletionState"], string> = {
  "auth-deleted":
    "Sign-in account deleted; Host cleanup incomplete. Contact support to finish cleanup using the account ID above. Do not retry deletion or create a replacement account.",
  "manual-review":
    "Deletion paused for manual review; conflicting or invalid data retained. Sign-in account remains; normal operations are blocked. Contact support with the account ID above for operator resolution before resuming with this same account. Do not retry now or create a replacement account.",
};

function deletionError(error: unknown): string {
  if (error instanceof ControlApiError) {
    if (error.code === "UNAUTHENTICATED") {
      return "Authentication unverified; this does not confirm whether deletion completed. After interrupted deletion, sign-in may no longer work: contact support if so. Otherwise, sign in to the same account and retry within five minutes. Do not create a replacement account or retry with a new account ID. Session not cleared.";
    }
    if (error.code === "UNAVAILABLE") {
      return "Deletion unavailable or incomplete; some data may be removed. Wait before retrying; only resume with the same Firebase account ID. If sign-in fails, contact support; do not create a replacement account. Local bypass and static test tokens are unsupported.";
    }
    if (error.status >= 400 && error.status < 500) {
      return `${error.message} Your session has not been cleared. Resolve the error before retrying.`;
    }
  }
  return "Deletion could not be confirmed; it may still complete or some account data may already have been removed. Session not cleared. Wait before retrying with this same account. If sign-in fails, contact support to verify completion; do not create a replacement account.";
}

export function AccountPanel() {
  const { user } = useAuth();
  return <AccountSettings key={user?.uid ?? "signed-out"} />;
}

function AccountSettings() {
  const { user, profile, access, accessSource, signOut, error } = useAuth();
  const api = useControlApiClient();
  const { state: capacity } = useAccountCapacity();
  const navigate = useNavigate();
  const [confirmation, setConfirmation] = useState("");
  const [pending, setPending] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [supportRequired, setSupportRequired] = useState<
    DeleteAccountErrorDetails["deletionState"] | null
  >(null);
  const active = useRef(true);
  useLayoutEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const [message, setMessage] = useState<string | null>(null);
  const busy = useRef(false);
  const unavailable =
    accessSource === "local" ||
    Boolean(getRequiredWebEnv().controlApiToken) ||
    user?.providerId !== "firebase";
  const providers = [...new Set(user?.providerData?.map((provider) => provider.providerId) ?? [])];

  async function leaveConsole() {
    if (busy.current) return;
    busy.current = true;
    setPending(true);
    setMessage(null);
    try {
      await signOut();
      if (active.current) void navigate("/", { replace: true });
    } catch {
      if (!active.current) return;
      setMessage(
        deleted
          ? "Your account was deleted, but browser sign-out failed. Retry sign out to clear this session. Do not submit deletion again."
          : "Unable to sign out. Please try again."
      );
    } finally {
      busy.current = false;
      if (active.current) setPending(false);
    }
  }

  async function deleteAccount(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy.current || unavailable || deleted || supportRequired || confirmation !== "DELETE")
      return;
    busy.current = true;
    setPending(true);
    setMessage(null);
    let confirmed = false;
    try {
      await api.deleteAccount({ confirmation: "DELETE" });
      if (!active.current) return;
      confirmed = true;
      setDeleted(true);
      setConfirmation("");
      await signOut();
      if (active.current) void navigate("/", { replace: true });
    } catch (failure) {
      if (!active.current) return;
      const supportReason = !confirmed ? deletionSupportReason(failure) : null;
      if (supportReason) {
        setSupportRequired(supportReason);
        setConfirmation("");
        return;
      }
      setMessage(
        confirmed
          ? "Your account was deleted, but browser sign-out failed. Retry sign out to clear this session. Do not submit deletion again."
          : deletionError(failure)
      );
    } finally {
      busy.current = false;
      if (active.current) setPending(false);
    }
  }

  return (
    <div className="settings-layout">
      <section className="panel settings-panel settings-profile" aria-labelledby="settings-profile">
        <div>
          <h2 className="panel-title" id="settings-profile">
            Profile
          </h2>
          <p className="form-note">
            Your current account identity. Profile details are read-only here.
          </p>
        </div>
        <dl className="settings-field-grid">
          <div>
            <dt className="field-label">Display name</dt>
            <dd>{profile?.displayName ?? user?.displayName ?? "Not provided"}</dd>
          </div>
          <div>
            <dt className="field-label">Email</dt>
            <dd>{profile?.email ?? user?.email ?? "Not provided"}</dd>
          </div>
          <div className="settings-field-wide">
            <dt className="field-label">Account ID</dt>
            <dd className="cell-mono">{user?.uid ?? "Not available"}</dd>
          </div>
        </dl>
      </section>

      <section className="panel settings-panel" aria-labelledby="settings-usage">
        <h2 className="panel-title" id="settings-usage">
          Usage
        </h2>
        <dl className="settings-field-grid">
          <div>
            <dt className="field-label" id="settings-capacity-label">
              Capacity allocation <CapacityTooltip />
            </dt>
            <dd>
              {capacity.status === "ready"
                ? `${capacity.data.allocatedCapacity} / ${capacity.data.totalCapacity} capacity units`
                : capacity.status === "loading"
                  ? "Loading…"
                  : "Unavailable"}
            </dd>
          </div>
        </dl>
        {capacity.status === "ready" ? (
          <progress
            className="settings-ccu-bar"
            aria-label="Capacity allocation"
            aria-describedby="settings-capacity-note"
            aria-valuetext={`${capacity.data.allocatedCapacity} / ${capacity.data.totalCapacity} capacity units allocated`}
            max={1}
            value={
              capacity.data.totalCapacity > 0
                ? Math.min(capacity.data.allocatedCapacity / capacity.data.totalCapacity, 1)
                : capacity.data.allocatedCapacity > 0
                  ? 1
                  : 0
            }
          />
        ) : null}
        <p className="form-note" id="settings-capacity-note">
          Capacity = Hz × allocated CCU. This is reserved capacity, not live usage or billing.
        </p>
      </section>

      <section className="panel settings-panel" aria-labelledby="settings-access">
        <h2 className="panel-title" id="settings-access">
          Access
        </h2>
        <dl className="settings-field-grid">
          <div>
            <dt className="field-label">Access level</dt>
            <dd>{access.charAt(0).toUpperCase() + access.slice(1)}</dd>
          </div>
          <div>
            <dt className="field-label">Source</dt>
            <dd>
              {accessSource === "firestore"
                ? "Account entitlement"
                : accessSource === "local"
                  ? "Local admin bypass"
                  : accessSource === "mock"
                    ? "Mock development access"
                    : "Default access"}
            </dd>
          </div>
        </dl>
        <p className="form-note">
          Access is restricted to Lite, while we go through alpha release.
        </p>
        {error ? (
          <p className="form-error" role="alert">
            {error.message}
          </p>
        ) : null}
      </section>

      <section className="panel settings-panel" aria-labelledby="settings-providers">
        <h2 className="panel-title" id="settings-providers">
          Providers
        </h2>
        {providers.length ? (
          <ul className="settings-providers">
            {providers.map((provider) => (
              <li key={provider}>{providerNames[provider] ?? provider}</li>
            ))}
          </ul>
        ) : (
          <p className="form-note">No linked provider details available.</p>
        )}
        <p className="form-note">
          Linked sign-in methods are shown for reference. Provider linking and credentials are not
          managed here.
        </p>
        {accessSource !== "local" ? (
          <div>
            <button
              className="btn btn--danger btn--sm"
              type="button"
              disabled={pending}
              onClick={() => void leaveConsole()}
            >
              Sign Out
            </button>
          </div>
        ) : null}
      </section>

      <section
        className="panel settings-panel settings-danger"
        aria-labelledby="settings-danger"
        aria-busy={pending}
      >
        <div>
          <h2 className="panel-title" id="settings-danger">
            Danger zone
          </h2>
          <p id="delete-scope" className="form-note">
            Permanently delete your sign-in, profile, access, owned servers and Host logs.
            Irreversible.
          </p>
        </div>

        {unavailable ? (
          <p className="form-note" id="delete-unavailable">
            Sign in with Firebase without local bypass or a static test token to delete.
          </p>
        ) : null}
        {supportRequired ? (
          <p className="form-error" role="alert">
            {cleanupSupportMessages[supportRequired]}
          </p>
        ) : deleted ? (
          <p role="status">
            Account deletion confirmed. Sign out to finish clearing this browser session.
          </p>
        ) : (
          <form className="form" onSubmit={(event) => void deleteAccount(event)}>
            <div className="field">
              <label className="field-label" htmlFor="delete-confirmation">
                Type DELETE to confirm
              </label>
              <input
                className="input"
                id="delete-confirmation"
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                disabled={pending || unavailable}
                autoComplete="off"
                spellCheck={false}
                aria-describedby={`delete-scope${unavailable ? " delete-unavailable" : ""}`}
              />
            </div>
            <div>
              <button
                className="btn btn--danger"
                type="submit"
                disabled={pending || unavailable || confirmation !== "DELETE"}
              >
                {pending ? "Please Wait…" : "Delete Account Permanently"}
              </button>
            </div>
          </form>
        )}
        {pending ? (
          <p role="status">Please wait. Keep this page open until the operation finishes.</p>
        ) : null}
        {message ? (
          <p className="form-error" role="alert">
            {message}
          </p>
        ) : null}
      </section>
    </div>
  );
}
